import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Info } from "lucide-react";
import { api } from "../lib/api";
import { usePaged, LoadMore } from "../lib/paged";
import { Loading } from "../components/common";
import { Notice } from "../components/kit";
import { uiLang } from "../components/ProductCard";
import { Band, Cta, CtaBand, DirChip, EdShell, Hero, IMG } from "../components/Editorial";
import { ExploreDemo } from "../components/PublicHeader";

const byline = (c) => c.author_label || String(c.author_name || "").replace(/\s*\(placeholder\)\s*$/, "");
const PLACEHOLDER = /^(Placeholder copy|Текст-заглушка)/;

function KindBadge({ c, testid, big }) {
  const { t } = useTranslation();
  const ill = c.kind === "illustrative";
  return <span className={`inline-flex w-fit items-center gap-1.5 rounded-full border font-bold ${big ? "px-3 py-1 text-sm" : "px-2.5 py-0.5 text-xs"} ${ill ? "border-amber-line bg-amber text-amber-ink" : "border-sage-line bg-sage text-sage-ink"}`} data-testid={testid}>{ill && <Info className="h-3.5 w-3.5" aria-hidden />}{t(`b3.kind_${c.kind}`)}</span>;
}

export function PublicCases() {
  const { t, i18n } = useTranslation();
  const pg = usePaged("/public/cases", { lang: uiLang(i18n) }, 12);
  return (
    <EdShell testid="public-cases">
      <Hero testid="cases-hero" eyebrow={t("b8.cases_eyebrow")} title={t("b3.cases")} lead={t("b3.cases_sub")} img={IMG("cases")} alt={t("b8.cases_alt")}
        ctas={<><Cta primary to="/about/learning" testid="cases-cta-courses">{t("b8.view_courses")}</Cta><ExploreDemo testid="cases-cta-demo" className="max-sm:whitespace-normal" /></>} />
      <Band testid="cases-grid-band">
        {!pg.items ? <Loading /> : <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{pg.items.map((c, i) => (
          <li key={c.id} className="rv-item min-w-0" style={{ "--i": i % 6 }}><Link to={`/cases/${c.id}`} data-testid={`public-case-${c.number}`} className="lift surface flex h-full flex-col overflow-hidden">
            <img src={`/img/b9/cover_${String((c.directions || [])[0] || "a").toLowerCase()}.webp`} alt="" aria-hidden loading="lazy" className="aspect-[16/8] w-full object-cover" data-testid={`public-case-img-${c.number}`} />
            <div className="flex flex-1 flex-col p-6">
            <KindBadge c={c} testid={`public-case-kind-${c.number}`} />
            <h2 className="mt-4 font-display text-lg font-semibold text-ink">{c.edition?.title}</h2>
            {c.edition?.observation_period && <p className="mt-2 text-sm text-clay-strong">{c.edition.observation_period}</p>}
            {c.edition?.summary && !PLACEHOLDER.test(c.edition.summary) && <p className="mt-2 line-clamp-3 text-sm text-neutral-ink">{c.edition.summary}</p>}
            <div className="mt-4 flex flex-wrap gap-2">{(c.directions || []).map((k) => <DirChip key={k} k={k} />)}</div>
            <p className="mt-auto flex items-center justify-between gap-2 pt-5 text-sm"><span className="text-muted-foreground" data-testid={`public-case-author-${c.number}`}>{byline(c)}</span><span className="inline-flex items-center gap-1 font-semibold text-clay-strong">{t("b8.read_case")}<ArrowRight className="h-4 w-4" aria-hidden /></span></p>
            </div>
          </Link></li>))}</ul>}
        <LoadMore shown={(pg.items || []).length} total={pg.total} onMore={pg.more} loading={pg.loading} testid="cases" />
      </Band>
      <CtaBand />
    </EdShell>
  );
}

function Part({ k, title, text }) {
  if (!text || PLACEHOLDER.test(text)) return null;
  return <section className="rv-item border-t border-sand-line pt-6" data-testid={`case-${k}`}><h2 className="font-display text-xl font-semibold text-ink">{title}</h2><p className="mt-3 whitespace-pre-wrap text-neutral-ink">{text}</p></section>;
}

export function PublicCase() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const lg = uiLang(i18n);
  const [c, setC] = useState(null);
  useEffect(() => { api.get(`/public/cases/${id}`, { params: { lang: lg } }).then((r) => setC(r.data)).catch(() => setC(false)); }, [id, lg]);
  const back = <Link to="/cases" data-testid="case-back" className="link-more"><ArrowLeft className="h-4 w-4" aria-hidden />{t("b8.back_cases")}</Link>;
  if (c === false || c?.status === "withdrawn" || !c) return <EdShell testid="public-case"><div className="py-10">{back}<div className="mt-4">{c === false ? <Notice tone="warn" testid="b3-not-found">{t("b3.not_found")}</Notice> : c ? <Notice tone="warn" testid="case-withdrawn">{t("b3.case_withdrawn")}</Notice> : <Loading />}</div></div></EdShell>;
  const e = c.edition || {};
  const d0 = (c.directions || [])[0];
  return (
    <EdShell testid="public-case">
      <article className="pt-6 lg:pt-10">
        {back}
        {c.kind === "illustrative" && <div role="note" className="fade-up mt-5 flex items-start gap-3 rounded-2xl border-2 border-amber-line bg-amber-soft p-4 text-ink" data-testid="case-illustrative-banner"><Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-ink" aria-hidden /><p className="font-semibold">{t("b8.illustrative_note")}</p></div>}
        <div className="fade-up mt-6 flex flex-wrap items-center gap-2"><KindBadge c={c} big testid="case-kind-label" /></div>
        <h1 className="fade-up mt-4 max-w-4xl break-words font-display text-4xl font-semibold text-ink sm:text-5xl" data-testid="case-title">{e.title}</h1>
        <p className="mt-4 text-neutral-ink" data-testid="case-author">{t("b8.by")}: <b className="text-ink">{byline(c)}</b></p>
        {c.lang_notice && <div className="mt-4"><Notice tone="warn">{t("cat.lang_missing", { lang: t(`lang.${lg}`) })}</Notice></div>}
        <div className="mt-10 grid gap-10 lg:grid-cols-12">
          <div className="min-w-0 space-y-8 lg:col-span-8">
            <Part k="summary" title={t("b3.cf_summary")} text={e.summary} />
            <Part k="outcomes" title={t("b3.cf_outcomes")} text={e.outcomes} />
            <Part k="limitations" title={t("b3.cf_limitations")} text={e.limitations} />
            {!!c.photos?.length && <div className="grid gap-4 sm:grid-cols-2">{c.photos.map((p) => <figure key={p.index}><img loading="lazy" src={`${process.env.REACT_APP_BACKEND_URL}/api/public/cases/${c.id}/photos/${p.index}`} alt={p.caption || t("b3.photo_n", { n: p.index + 1 })} className="w-full rounded-2xl" /><figcaption className="mt-1 text-sm text-neutral-ink">{p.caption}</figcaption></figure>)}</div>}
          </div>
          <aside className="min-w-0 space-y-5 lg:col-span-4">
            <div className="surface space-y-5 p-6 lg:sticky lg:top-24">
              {e.observation_period && <div data-testid="case-observation_period"><p className="eyebrow">{t("b8.period")}</p><p className="mt-1 text-ink">{e.observation_period}</p></div>}
              {!!c.directions?.length && <div data-testid="case-directions"><p className="eyebrow">{t("b8.approach_links")}</p><div className="mt-2 flex flex-wrap gap-2">{c.directions.map((k) => <DirChip key={k} k={k} to={`/about/methodology#dir-${k}`} testid={`case-direction-${k}`} />)}</div></div>}
              <div className="flex flex-col gap-3 border-t border-sand-line pt-5">
                {d0 && <Link to={`/about/learning#course-COURSE-${d0}`} data-testid="case-related-course" className="btn btn-ink">{t("b8.related_course")}<ArrowRight className="h-4 w-4" aria-hidden /></Link>}
                <ExploreDemo testid="case-demo-cta" className="max-sm:whitespace-normal" />
              </div>
            </div>
          </aside>
        </div>
      </article>
      <CtaBand />
    </EdShell>
  );
}
